import reactStringReplace from 'react-string-replace';

// NOTE: intentionally using v8 versions sicne styling, including fontTypes etc. is returned by RPro backend
import { Anchor, BUTTON_TYPES, FONT_TYPES, TextButton } from '@zillow/constellation';
import { Button, Text } from '@zillow/constellation-10';
import { startCase } from '@zg-rentals/zdash';

import { preWrapTextStyles } from '../styles';
import StringConstants from '../constants/StringConstants';

import type { LocaleSpecificNoticeProps } from '../model/LocaleSpecificNotice';
import type { TotalMonthlyPriceWithFeeNoticeProps } from '../model/TotalMonthlyPriceWithFeeNotice';
import type { ComponentProps } from 'react';

export type BuildTextArgs = {
  text: string;
  stylings: Array<string>;
  key: string;
  defaultFontType: string;
  url?: string | null;
  onClick?: (() => void) | null;
  testId?: string | null;
  fsHide?: boolean;
};

const stringHelper = {
  isValidUrl(str: string) {
    let url;
    try {
      url = new URL(str);
    } catch (_) {
      return false;
    }
    return url.protocol === 'http:' || url.protocol === 'https:';
  },
  capitalize(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  },
  pluralize(num: number | string, singular: string, plural: string) {
    // sometimes this function receives a value of "-" which we want to format as plural
    const parsedNum = typeof num === 'string' ? parseInt(num, 10) : num;
    return `${num} ${parsedNum === 1 ? singular : plural}`;
  },
  pluralizeWord(num: number, singular: string, plural: string) {
    return `${num === 1 ? singular : plural}`;
  },
  getPageTitle(titleContent: string, brand: keyof typeof StringConstants.FORMATTED_BRAND_NAMES_RENTAL) {
    const formattedBrand = StringConstants.FORMATTED_BRAND_NAMES_RENTAL[brand];
    return `${titleContent} | ${formattedBrand}`;
  },
  wrapText(text = '', maxCharacters: number) {
    return `${text.slice(0, maxCharacters)}${text.length >= maxCharacters ? '…' : ''}`;
  },
  underScoreToSpaces(str = '') {
    return str.replace(/_/g, ' ');
  },
  getUrlQueryParams() {
    if (typeof window === 'undefined') {
      return false;
    }

    const query = window.location.search.substring(1);

    if (!query) {
      return false;
    }
    const queryString = {};
    const vars = query.split('&');
    let i;
    let pair: Array<string>;

    for (i = 0; i < vars.length; i++) {
      pair = vars[i].split('=');
      // @ts-expect-error
      if (typeof queryString[pair[0]] === 'undefined') {
        // @ts-expect-error
        queryString[pair[0]] = decodeURIComponent(pair[1]);
      } else if (
        // @ts-expect-error
        typeof queryString[pair[0]] === 'string'
      ) {
        // @ts-expect-error
        queryString[pair[0]] = [queryString[decodeURIComponent(pair[0])], decodeURIComponent(pair[1])];
      } else {
        // @ts-expect-error
        queryString[pair[0]].push(decodeURIComponent(pair[1]));
      }
    }
    return queryString;
  },
  getLastPieceFromPath(pathname: string) {
    if (!pathname) {
      return '';
    }

    return pathname.split(/[/]+/).pop();
  },
  sanitizeReactInput(string: string) {
    let parsed = '';

    // react cannot inject "{" gracefully in strings
    // e.g. <div>`${"{"}`</div> will break
    if (string) {
      parsed = string.replace(/\{|\}/g, '');
    }

    return parsed;
  },
  /**
   * startCaseRemoveSpace
   *
   * @param {string} string
   *
   * ex: foo bar
   * => FooBar
   */
  startCaseRemoveSpace(string: string) {
    return typeof string === 'string' ? startCase(string).replace(/\s/g, '') : '';
  },
  updateFontHeading(fontType: string) {
    switch (fontType) {
      case FONT_TYPES.body:
        return FONT_TYPES.bodyHeading;
      case FONT_TYPES.bodySmall:
        return FONT_TYPES.bodySmallHeading;
      case FONT_TYPES.finePrint:
        return FONT_TYPES.finePrintHeading;
      default:
        return fontType;
    }
  },
  getFontType(defaultFontType: string, stylings: Array<string>) {
    let fontType = stylings.filter((style: string) => FONT_TYPES.hasOwnProperty(style))[0];
    if (!fontType) {
      fontType = defaultFontType;
    }
    if (stylings.length > 0 && stylings.includes('emphasize')) {
      fontType = stringHelper.updateFontHeading(fontType);
    }
    return fontType;
  },
  isEmailString(url: string) {
    return url.includes('@') && url.includes('.com') && !url.includes('/');
  },
  mapFontTypeToTextStyle(fontType: string): ComponentProps<typeof Text>['textStyle'] {
    // Map v8 fontTypes (from backend) to v10 textStyles
    const mapping: Record<string, ComponentProps<typeof Text>['textStyle']> = {
      [FONT_TYPES.h1]: 'heading-xl',
      [FONT_TYPES.h2]: 'heading-lg',
      [FONT_TYPES.h3]: 'heading-md',
      [FONT_TYPES.h4]: 'heading-sm',
      [FONT_TYPES.h5]: 'heading-xs',
      [FONT_TYPES.h6]: 'body-lg',
      [FONT_TYPES.body]: 'body',
      [FONT_TYPES.bodyHeading]: 'body-bold',
      [FONT_TYPES.bodySmall]: 'body-sm',
      [FONT_TYPES.bodySmallHeading]: 'body-sm-bold',
      [FONT_TYPES.finePrint]: 'body-xs',
      [FONT_TYPES.finePrintHeading]: 'body-xs-bold',
    };
    return mapping[fontType] || 'body';
  },
  buildText({
    text,
    stylings,
    key,
    defaultFontType,
    url = null,
    onClick = () => {},
    testId = null,
    fsHide = false,
  }: BuildTextArgs) {
    const fontType = stringHelper.getFontType(defaultFontType, stylings);
    const buttonType: Array<keyof typeof BUTTON_TYPES | string> = stylings.filter((style: string) =>
      BUTTON_TYPES.hasOwnProperty(style),
    );
    const propsForButton = {
      key,
      onClick: onClick || undefined,
      'data-test': testId,
      'data-testid': testId,
      disabled: stylings.length > 0 ? stylings.includes('disabled') : false,
      fontType,
      ...(buttonType.length > 0 && {
        tone: (buttonType[0] === 'primary' ? 'brand' : 'brand') as 'brand' | 'neutral' | 'critical',
        emphasis: (buttonType[0] === 'primary' ? 'filled' : 'outlined') as 'filled' | 'outlined',
      }),
    };

    if ((stylings.includes('primary') || stylings.includes('secondary')) && !stylings.includes('textLink')) {
      return <Button {...propsForButton}>{text}</Button>;
    }

    return (
      <Text
        textStyle={stringHelper.mapFontTypeToTextStyle(fontType)}
        onClick={onClick || undefined}
        className={fsHide ? 'fs-mask' : undefined}
        key={key}
        css={preWrapTextStyles}
      >
        {url ? (
          stringHelper.isEmailString(url) ? (
            <Anchor {...propsForButton} href={`mailto:${url}`} target="_blank">
              {text}
            </Anchor>
          ) : (
            <Anchor {...propsForButton} href={url} target="_blank">
              {text}
            </Anchor>
          )
        ) : stylings.includes('textLink') ? (
          <TextButton {...propsForButton}>{text}</TextButton>
        ) : (
          text
        )}
      </Text>
    );
  },
  kebabize(string: string) {
    return string
      .split('')
      .map((letter: string, idx: number) => {
        return letter.toUpperCase() === letter ? `${idx !== 0 ? '-' : ''}${letter.toLowerCase()}` : letter;
      })
      .join('');
  },
  renderWithReplacedLinks(
    { text, mappings }: LocaleSpecificNoticeProps | TotalMonthlyPriceWithFeeNoticeProps,
    {
      callbacks = {},
      dataEventIds = {},
    }: { callbacks?: { [x: string]: () => void }; dataEventIds?: Record<string, string> } = {},
    generic?: string,
  ) {
    let formattedText: React.ReactNodeArray | string = text;

    mappings.forEach((item) => {
      const { replaceUrl, replaceText } = item;
      const keyForMaps = replaceText === generic ? replaceText : replaceText.split(' ').slice(1).join(' ');

      // replace with link component
      formattedText = reactStringReplace(formattedText, replaceText, () => (
        <Anchor
          href={replaceUrl}
          target="_blank"
          onClick={callbacks?.[keyForMaps]}
          data-eventid={dataEventIds?.[keyForMaps]}
        >
          {replaceText}
        </Anchor>
      ));
    });

    return formattedText;
  },
};

export default stringHelper;
